import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowLeft,
  Search,
  BarChart3,
  MonitorSmartphone,
  ShieldCheck,
  MapPinned,
  MessageSquare,
  CheckCircle2,
  TrendingUp,
  Zap,
  Activity,
  Globe,
  Send,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

/* ───── data ───── */
const capabilities = [
  { icon: Search, title: "SEO ונראות בגוגל", desc: "שיפור דירוגים, תיקון בעיות טכניות, ומציאת הזדמנויות חיפוש חדשות." },
  { icon: BarChart3, title: "קמפיינים והמרות", desc: "שיפור קמפיינים, הורדת עלויות, והגדלת כמות הפניות האיכותיות." },
  { icon: MonitorSmartphone, title: "אתר ותוכן", desc: "שיפור חוויית המשתמש, תוכן, מהירות ומסלולי המרה באתר." },
  { icon: ShieldCheck, title: "סמכות ואמון", desc: "חיזוק הנוכחות מול מתחרים, בניית קישורים וחיזוק אמינות." },
  { icon: MapPinned, title: "ביקורות ומקומי", desc: "ניהול Google Business, ביקורות, ושיפור הנראות המקומית." },
  { icon: MessageSquare, title: "רשתות חברתיות", desc: "פרסום, תגובות, לייקים ומעורבות חכמה בפייסבוק ואינסטגרם." },
];

const steps = [
  { title: "מתחברים", desc: "אונבורדינג קצר שמחבר את Google, האתר ומקורות השיווק." },
  { title: "מגדירים", desc: "בוחרים שפה, קהל יעד, שירותים, אזורים ורמת פעילות." },
  { title: "המערכת עובדת", desc: "הכלים רצים במקביל, משתפרים ומייצרים תוצאות באופן רציף." },
];

const pricingCards = [
  {
    name: "בסיסי",
    audience: "עסקים קטנים",
    features: [
      "שיפור SEO ונראות אורגנית",
      "אופטימיזציה לאתר ותוכן",
      "ניהול Google Business",
      "ביקורות וחיזוק מוניטין",
      "פעילות חכמה ברשתות חברתיות",
      "דוחות ומעקב ביצועים",
    ],
  },
  {
    name: "מתקדם",
    audience: "עסקים בצמיחה",
    featured: true,
    features: [
      "הכל מהחבילה הבסיסית",
      "ניהול ואופטימיזציית קמפיינים ממומנים",
      "שיפור המרות ודפי נחיתה",
      "מעורבות חברתית מתקדמת",
      "ניתוח מתחרים והשוואת סמכות",
      "סנכרון בין כל הערוצים בזמן אמת",
    ],
  },
  {
    name: "פרימיום",
    audience: "לקוחות גדולים עם תחרות רבה",
    features: [
      "הכל מהחבילה המתקדמת",
      "פעילות אגרסיבית על סמכות ונוכחות",
      "כיסוי מקסימלי של ערוצים ופלטפורמות",
      "השוואה עמוקה ושוטפת למתחרים",
      "אסטרטגיה מותאמת וניהול מלא",
      "עדיפות בתגובה ותמיכה",
    ],
  },
];

const timeline = [
  { title: "תוך ימים", desc: "חיבור המערכות, מיפוי מצב נוכחי וזיהוי בעיות ראשוניות." },
  { title: "תוך שבועות", desc: "תיקונים, שיפור נראות, תוכן והמרות. פעילות חכמה בכל הערוצים." },
  { title: "תוך חודשים", desc: "יתרון מול מתחרים, צמיחה יציבה ונוכחות חזקה יותר." },
];

/* ───── live AI feed simulation ───── */
const feedMessages = [
  { action: "SEO", detail: "זוהתה הזדמנות דירוג חדשה", icon: "search" },
  { action: "אתר", detail: "מהירות טעינה שופרה ב-23%", icon: "zap" },
  { action: "ביקורות", detail: "ביקורת חדשה זוהתה, תגובה נשלחה", icon: "star" },
  { action: "קמפיין", detail: "עלות לליד ירדה ב-18%", icon: "chart" },
  { action: "חברתי", detail: "תגובה חכמה פורסמה בפייסבוק", icon: "msg" },
  { action: "סמכות", detail: "קישור חדש זוהה מאתר סמכותי", icon: "shield" },
  { action: "SEO", detail: "3 עמודים עלו לעמוד 1 בגוגל", icon: "search" },
  { action: "אתר", detail: "שיפור UX, שיעור המרה 12%+", icon: "zap" },
  { action: "מקומי", detail: "Google Business Profile עודכן", icon: "map" },
  { action: "קמפיין", detail: "מודעה חלשה הושבתה אוטומטית", icon: "chart" },
];

const feedIcons: Record<string, React.FC<{className?: string}>> = {
  search: Search, zap: Zap, star: Activity, chart: BarChart3,
  msg: MessageSquare, shield: ShieldCheck, map: MapPinned,
};

/* ───── animated counter ───── */
function AnimatedCounter({ target, suffix = "" }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 2000;
    const step = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, target]);

  return <span ref={ref}>{count}{suffix}</span>;
}

/* ───── live feed component ───── */
function LiveFeed() {
  const [items, setItems] = useState<typeof feedMessages>([]);
  const idxRef = useRef(0);

  useEffect(() => {
    const interval = setInterval(() => {
      idxRef.current = (idxRef.current + 1) % feedMessages.length;
      setItems((old) => [feedMessages[idxRef.current], ...old].slice(0, 5));
    }, 2800);
    setItems([feedMessages[0]]);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-2 overflow-hidden" style={{ direction: "rtl" }}>
      <AnimatePresence mode="popLayout">
        {items.map((item, i) => {
          const Icon = feedIcons[item.icon] || Zap;
          return (
            <motion.div
              key={`${item.action}-${item.detail}-${i}`}
              initial={{ opacity: 0, x: 40, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -40, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="flex items-center gap-3 rounded-xl border border-[var(--line-strong)] bg-surface backdrop-blur-sm px-4 py-3"
            >
              <div className="shrink-0 rounded-lg bg-signal/15 p-2">
                <Icon className="h-4 w-4 text-signal" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-signal">{item.action}</div>
                <div className="truncate text-sm text-fg">{item.detail}</div>
              </div>
              <div className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-signal" />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}


/* ───── scroll-reveal wrapper ───── */
function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ───── WhatsApp lead form ───── */
function LeadCapture() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [domain, setDomain] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = encodeURIComponent(
      `היי אלעד, אשמח לשמוע עוד על Growth OS.\nשם: ${name}\nטלפון: ${phone}\nכתובת אתר: ${domain || "לא צוין"}\nנשלח מ: eladdigital.vercel.app/growth-os`
    );
    window.open(`https://wa.me/972557070007?text=${msg}`, "_blank");
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-md space-y-4" dir="rtl">
      <input
        type="text"
        required
        placeholder="השם שלך"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full rounded-sm border border-[var(--line-strong)] bg-surface px-4 py-3.5 text-fg placeholder:text-fg-3 outline-none transition-colors focus-visible:border-signal"
      />
      <input
        type="tel"
        required
        placeholder="מספר טלפון"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        className="w-full rounded-sm border border-[var(--line-strong)] bg-surface px-4 py-3.5 text-fg placeholder:text-fg-3 outline-none transition-colors focus-visible:border-signal"
        dir="ltr"
      />
      <input
        type="text"
        placeholder="כתובת האתר שלך (לא חובה)"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        className="w-full rounded-sm border border-[var(--line-strong)] bg-surface px-4 py-3.5 text-fg placeholder:text-fg-3 outline-none transition-colors focus-visible:border-signal"
        dir="ltr"
      />
      <motion.button
        type="submit"
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="btn btn-primary w-full px-6 py-3.5 text-base"
      >
        {sent ? (
          <>
            <CheckCircle2 className="h-5 w-5" />
            נשלח בהצלחה!
          </>
        ) : (
          <>
            <Send className="h-5 w-5" />
            שלחו לי פרטים בוואטסאפ
          </>
        )}
      </motion.button>
    </form>
  );
}

/* ───── particles background ───── */
function ParticleField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const particles: { x: number; y: number; vx: number; vy: number; size: number; alpha: number }[] = [];
    const count = 60;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 2 + 0.5,
        alpha: Math.random() * 0.4 + 0.1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.offsetWidth;
        if (p.x > canvas.offsetWidth) p.x = 0;
        if (p.y < 0) p.y = canvas.offsetHeight;
        if (p.y > canvas.offsetHeight) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(168, 85, 247, ${p.alpha})`;
        ctx.fill();
      }

      // draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(168, 85, 247, ${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ opacity: 0.7 }}
    />
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function GrowthOSPage() {
  // GSAP scroll animations
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // parallax stats
      gsap.utils.toArray<HTMLElement>(".stat-card").forEach((el, i) => {
        gsap.from(el, {
          scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" },
          y: 50,
          opacity: 0,
          duration: 0.6,
          delay: i * 0.1,
        });
      });

      // pricing cards stagger
      gsap.utils.toArray<HTMLElement>(".pricing-card").forEach((el, i) => {
        gsap.from(el, {
          scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
          y: 60,
          opacity: 0,
          scale: 0.95,
          duration: 0.7,
          delay: i * 0.15,
        });
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef} className="min-h-screen bg-ink text-fg overflow-x-clip">
      {/* ===== HERO ===== */}
      <section className="relative isolate border-b border-[var(--line-strong)] overflow-hidden">
        <ParticleField />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,229,160,0.10),transparent_45%),radial-gradient(circle_at_70%_80%,rgba(77,216,232,0.06),transparent_40%)]" />

        <div className="relative mx-auto grid max-w-7xl gap-8 px-6 pb-20 pt-28 md:grid-cols-2 md:gap-16 md:pb-28 md:pt-36">
          {/* text side */}
          <div className="flex flex-col justify-center text-center md:text-right" style={{ direction: "rtl" }}>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-4xl font-bold leading-tight md:text-6xl lg:text-7xl"
            >
              מערכת צמיחה
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="block text-signal"
              >
                אוטונומית
              </motion.span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mx-auto mt-6 max-w-xl text-base leading-8 text-fg md:mx-0 md:text-lg"
            >
              מערכת אחת שמריצה פעולות במקביל: SEO, תוכן, אתר, פרסום, ביקורות,
              סמכות ומעורבות חברתית, ומייצרת תוצאות אמיתיות.
            </motion.p>

            {/* trust line */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-5 flex items-center justify-center gap-2 text-sm text-signal/90 md:justify-start"
            >
              <Activity className="h-4 w-4 animate-pulse" />
              <span>מערכת אחת. מאות פעולות במקביל. תוצאות אמיתיות.</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="mt-8 flex flex-wrap justify-center gap-3 md:justify-start"
            >
              <motion.a
                href="#lead-form"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                className="btn btn-primary px-7 py-4 text-base"
              >
                רוצה לשמוע עוד
                <ArrowLeft className="h-5 w-5" />
              </motion.a>
              <motion.a
                href="#how"
                whileHover={{ scale: 1.03 }}
                className="inline-flex items-center gap-2 rounded-sm border border-[var(--line-strong)] bg-surface px-7 py-4 text-base text-fg-2 backdrop-blur-sm transition hover:border-[var(--line-strong)] hover:bg-surface"
              >
                איך זה עובד
              </motion.a>
            </motion.div>
          </div>

          {/* visual side: cinematic video + live feed */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center justify-center gap-6"
          >
            <div className="relative mx-auto w-full max-w-[420px] overflow-hidden rounded-[24px] border border-[var(--line-strong)] shadow-lg">
              {/* poster carries the first frame, so the box shows the visual
                  immediately instead of an empty rectangle while the file
                  loads, or if autoplay is refused. */}
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                poster="/images/growth-os-poster.jpg"
                aria-hidden="true"
                className="block w-full rounded-[24px]"
              >
                <source src="/images/growth-os.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="w-full max-w-xs md:max-w-sm">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-signal/80" style={{ direction: "rtl" }}>
                <div className="h-2 w-2 animate-pulse rounded-full bg-signal" />
                פעולות בזמן אמת
              </div>
              <LiveFeed />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="border-b border-[var(--line-strong)] py-12" dir="rtl">
        <div className="mx-auto max-w-3xl px-6">
          <dl className="grid gap-px border border-[var(--line)] bg-[var(--line)] sm:grid-cols-3">
            {[
              { value: 30, suffix: "+", label: "כלים פעילים" },
              { value: 24, suffix: "/7", label: "עבודה רציפה" },
              { value: 100, suffix: "%", label: "מבוסס נתונים" },
            ].map((s) => (
              <div
                key={s.label}
                className="stat-card flex items-baseline gap-3 bg-ink px-5 py-6"
              >
                <dt className="t-data text-3xl text-signal" dir="ltr">
                  <AnimatedCounter target={s.value} suffix={s.suffix} />
                </dt>
                <dd className="text-sm text-fg-2">{s.label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ===== CAPABILITIES ===== */}
      <section className="border-b border-[var(--line-strong)] py-20" dir="rtl">
        <div className="mx-auto max-w-5xl px-6">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold md:text-5xl">מה המערכת עושה</h2>
              <p className="mt-4 text-base leading-8 text-fg">
                במקום ספקים שונים ועבודה ידנית, מערכת אחת שמקדמת את העסק בכל החזיתות.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {capabilities.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} delay={i * 0.08}>
                  <motion.div
                    whileHover={{ y: -6, borderColor: "rgba(0,229,160,0.35)" }}
                    className="group rounded-sm border border-[var(--line-strong)] bg-surface p-6 transition-colors"
                  >
                    <div className="mb-4 inline-flex rounded-xl bg-signal/12 p-3 text-signal transition-all group-hover:shadow-lg group-hover:shadow-md">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg font-semibold">{c.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-fg-2">{c.desc}</p>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== HOW TO START ===== */}
      <section id="how" className="relative border-b border-[var(--line-strong)] bg-surface py-20 overflow-hidden" dir="rtl">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <h2 className="text-center text-3xl font-bold md:text-5xl">איך מתחילים</h2>
          </Reveal>

          <div className="relative mt-14">
            {/* connecting line */}
            <div className="absolute right-1/2 top-0 hidden h-full w-px -translate-x-1/2 bg-gradient-to-b from-signal/30 via-halo/20 to-transparent md:block" />

            <div className="grid gap-8 md:grid-cols-3">
              {steps.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.15}>
                  <div className="relative rounded-sm border border-[var(--line-strong)] bg-surface p-7 text-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ type: "spring", stiffness: 200, delay: i * 0.2 }}
                      className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-signal text-lg font-bold text-[#04120c] shadow-md"
                    >
                      {i + 1}
                    </motion.div>
                    <h3 className="text-xl font-semibold">{s.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-fg-2">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="border-b border-[var(--line-strong)] py-20" dir="rtl">
        <div className="mx-auto max-w-5xl px-6">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold md:text-5xl">תמחור</h2>
              <p className="mt-4 text-base leading-8 text-fg">
                שלוש רמות פעילות, לפי היקף העבודה ומספר הכלים הפעילים. את המחיר
                נסגור בשיחה קצרה, לפי מה שהעסק שלכם צריך באמת ובלי חבילות מנופחות.
              </p>
            </div>
          </Reveal>

          {/* comparison callout */}
          <Reveal>
            <div className="mx-auto mt-10 max-w-2xl rounded-sm border border-signal/20 bg-signal/5 p-5 text-center">
              <p className="text-sm leading-7 text-signal/90">
                <span className="font-bold">לשים לב:</span> אצל רוב הסוכנויות התקציב הזה קונה ניהול
                קמפיין בגוגל בלבד.
                <br />
                כאן מקבלים מערכת מקיפה שעושה <span className="font-bold text-fg">הכל</span>: SEO, אתר, תוכן, סמכות, ביקורות, פרסום ומעורבות,
                במקביל, עם ידיעה מוחלטת מה קורה בכל קטגוריה, איך היא משפיעה על קטגוריות אחרות, וטיפול אוטומטי בכל דבר.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {pricingCards.map((card, tierIndex) => (
              <motion.div
                key={card.name}
                whileHover={{ y: -8 }}
                className={`pricing-card relative rounded-sm border p-7 transition-all ${
                  card.featured
                    ? "border-signal/30 bg-gradient-to-b from-signal/15 to-transparent shadow-md"
                    : "border-[var(--line-strong)] bg-surface"
                }`}
              >
                {card.featured && (
                  <div className="absolute -top-3 right-1/2 translate-x-1/2 whitespace-nowrap rounded-full bg-signal px-4 py-1 text-xs font-bold text-[#04120c] shadow-md">
                    הכי פופולרי
                  </div>
                )}
                <div className="flex items-baseline gap-3">
                  <span className="t-data text-xs text-signal" dir="ltr">
                    {String(tierIndex + 1).padStart(2, "0")}
                  </span>
                  <span className="text-xs font-medium tracking-widest text-fg-2">
                    {card.audience}
                  </span>
                </div>
                <h3 className="mt-2 text-2xl font-semibold">{card.name}</h3>
                <div className="mt-6 space-y-3">
                  {card.features.map((f) => (
                    <div key={f} className="flex items-start gap-2.5 rounded-xl bg-surface px-3 py-3 text-sm text-fg">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-signal" />
                      {f}
                    </div>
                  ))}
                </div>
                <motion.a
                  href="#lead-form"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--line-strong)] bg-surface py-3 text-sm font-semibold transition hover:bg-surface"
                >
                  התחילו עכשיו
                  <ArrowLeft className="h-4 w-4" />
                </motion.a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TIMELINE ===== */}
      <section className="border-b border-[var(--line-strong)] bg-surface py-20" dir="rtl">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <h2 className="text-center text-3xl font-bold md:text-5xl">מה לצפות</h2>
          </Reveal>

          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {timeline.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.12}>
                <div className="rounded-sm border border-[var(--line-strong)] bg-surface p-6 text-center">
                  <motion.div
                    whileHover={{ rotate: 10, scale: 1.1 }}
                    className="mx-auto mb-4 inline-flex rounded-xl bg-signal/12 p-3 text-signal"
                  >
                    <TrendingUp className="h-6 w-6" />
                  </motion.div>
                  <div className="text-lg font-bold">{t.title}</div>
                  <p className="mt-2 text-sm leading-7 text-fg-2">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LEAD CAPTURE CTA ===== */}
      <section id="lead-form" className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,229,160,0.09),transparent_50%)]" />
        <div className="relative mx-auto max-w-3xl px-6">
          <Reveal>
            <div className="rounded-sm border border-[var(--line-strong)] bg-signal/[0.06] p-8 text-center backdrop-blur-sm md:p-14">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 200 }}
                className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-sm bg-signal text-[#04120c] shadow-md"
              >
                <Globe className="h-8 w-8" />
              </motion.div>

              <h2 className="text-3xl font-bold leading-tight md:text-4xl" style={{ direction: "rtl" }}>
                מוכנים למנוע צמיחה חכם?
              </h2>

              <p className="mx-auto mt-4 max-w-xl text-base leading-8 text-fg" style={{ direction: "rtl" }}>
                השאירו פרטים ונחזור אליכם תוך שעות עם הצעה מותאמת, ישירות לוואטסאפ.
              </p>

              <div className="mt-8">
                <LeadCapture />
              </div>

              <div className="mt-6 flex items-center justify-center gap-4 text-xs text-fg-2" style={{ direction: "rtl" }}>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-signal/60" />
                  בלי התחייבות
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-signal/60" />
                  תגובה תוך שעות
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-signal/60" />
                  ישירות לוואטסאפ
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
